import { PLATFORM_OPTIONS } from '../../lib/platforms';
import type { OnboardingCreate, OnboardingParsedFields, PlatformName } from '../../types/api';
import { ALL_PRESET_GOALS, brandKindCopy, isBrandKind, type BrandKind } from './brandKind';

/**
 * The brand brief as the form holds it: every field present, strings empty
 * rather than absent, so the same shape works as a draft payload, a parse
 * result and the pristine reference `restoreDraft` compares against.
 *
 * `kind` rides in the draft so a refresh mid-setup reopens the same form.
 * `headline` is only shown, and only sent, for a personal brand.
 */
export interface BrandBriefValues {
  kind: BrandKind;
  business_name: string;
  headline: string;
  industry: string;
  value_proposition: string;
  target_audience: string;
  tone: string;
  tagline: string;
  never_say: string[];
  platforms: PlatformName[];
  goals: string[];
}

export const EMPTY_BRIEF: BrandBriefValues = {
  kind: 'business',
  business_name: '',
  headline: '',
  industry: '',
  value_proposition: '',
  target_audience: '',
  tone: '',
  tagline: '',
  never_say: [],
  platforms: [],
  goals: [],
};

/** The empty form for one kind. */
export function emptyBrief(kind: BrandKind): BrandBriefValues {
  return { ...EMPTY_BRIEF, kind };
}

/**
 * Field caps. Tighter than the backend's schema types on purpose - they are
 * the original wizard's limits, and the server's parser cuts to the same
 * numbers (`FIELD_CAPS` in `app/services/onboarding_parse.py`).
 */
export const BRIEF_CAPS = {
  business_name: 120,
  headline: 120,
  industry: 120,
  tone: 120,
  tagline: 200,
  value_proposition: 500,
  target_audience: 500,
} as const;

/** The parse route's body cap, mirrored so the textarea can say so before a 422. */
export const BRIEF_TEXT_CAP = 10_000;

const KNOWN_PLATFORMS: readonly string[] = PLATFORM_OPTIONS.map((option) => option.value);

function isPlatformName(value: string): value is PlatformName {
  return KNOWN_PLATFORMS.includes(value);
}

/** Wire slugs narrowed to the platforms the UI offers; anything else is dropped. */
export function toPlatformNames(values: readonly string[]): PlatformName[] {
  return values.filter(isPlatformName);
}

/**
 * A parsed goal that matches a preset chip of either kind, ignoring case,
 * becomes that chip rather than a duplicate custom token beside it.
 */
function canonicalGoal(goal: string): string {
  const match = ALL_PRESET_GOALS.find(
    (preset) => preset.toLowerCase() === goal.trim().toLowerCase(),
  );
  return match ?? goal.trim();
}

/** The form values a parse result prefills. Unknown platform slugs are dropped. */
export function fromParsedFields(
  kind: BrandKind,
  fields: OnboardingParsedFields,
): BrandBriefValues {
  return {
    kind,
    business_name: fields.business_name ?? '',
    headline: fields.headline ?? '',
    industry: fields.industry ?? '',
    value_proposition: fields.value_proposition ?? '',
    target_audience: fields.target_audience ?? '',
    tone: fields.tone ?? '',
    tagline: fields.tagline ?? '',
    never_say: [...fields.never_say],
    platforms: toPlatformNames(fields.platforms),
    goals: Array.from(new Set(fields.goals.map(canonicalGoal))).filter(Boolean),
  };
}

/** The one rule the server enforces too: a brand needs a name. */
export function validateBrief(values: BrandBriefValues): string | null {
  if (!values.business_name.trim()) {
    return values.kind === 'personal'
      ? 'Add your name to continue.'
      : 'Give the business a name to continue.';
  }
  return null;
}

/** The request body: trimmed, with every empty optional left out. */
export function toOnboardingCreate(values: BrandBriefValues): OnboardingCreate {
  const kind: BrandKind = isBrandKind(values.kind) ? values.kind : 'business';
  const body: OnboardingCreate = { business_name: values.business_name.trim(), kind };
  const headline = values.headline.trim();
  const industry = values.industry.trim();
  const valueProposition = values.value_proposition.trim();
  const targetAudience = values.target_audience.trim();
  const tone = values.tone.trim();
  const tagline = values.tagline.trim();
  if (kind === 'personal' && headline) body.headline = headline;
  if (industry) body.industry = industry;
  if (valueProposition) body.value_proposition = valueProposition;
  if (targetAudience) body.target_audience = targetAudience;
  if (tone) body.tone = tone;
  if (tagline) body.tagline = tagline;
  if (values.goals.length > 0) body.goals = values.goals;
  if (values.platforms.length > 0) body.platforms = values.platforms;
  if (values.never_say.length > 0) body.never_say = values.never_say;
  return body;
}

/** The preset goal values for a kind, for telling a chip from a custom token. */
export function presetGoalsFor(kind: BrandKind): readonly string[] {
  return brandKindCopy(kind).goals.map((option) => option.value);
}
